import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetActions, CNetResult, CNetHintBar } from '../other/_cnet';

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; }
function lcm(a, b) { return Math.abs(a * b) / gcd(a, b); }
function factorize(n) {
  const f = {};
  let x = Math.abs(n);
  for (let d = 2; d * d <= x; d++) while (x % d === 0) { f[d] = (f[d] || 0) + 1; x = x / d; }
  if (x > 1) f[x] = (f[x] || 0) + 1;
  return f;
}
function primeStr(n) {
  const f = factorize(n);
  const parts = Object.entries(f).flatMap(([p, e]) => Array(e).fill(p));
  return parts.length ? parts.join(' × ') : String(n);
}

export default function LCMCalc() {
  const [nums, setNums] = useState('330, 75, 450, 225');
  const [res, setRes] = useState(null);
  const calc = () => {
    const arr = nums.split(/[,\s]+/).map(x => parseInt(x, 10)).filter(x => !Number.isNaN(x));
    if (!arr.length) { setRes({ error: 'Enter at least one integer.' }); return; }
    const l = arr.reduce((a, b) => lcm(a, b));
    const g = arr.reduce((a, b) => gcd(a, b));
    // Highest-exponent primes union
    const facts = arr.map(factorize);
    const allPrimes = new Set();
    facts.forEach(f => Object.keys(f).forEach(p => allPrimes.add(p)));
    const maxPrimes = [...allPrimes].map(p => ({ p, e: Math.max(...facts.map(f => f[p] || 0)) }));
    setRes({ arr, l, g, facts, maxPrimes });
  };
  const clear = () => { setNums(''); setRes(null); };
  return (
    <CalcShell showActions={false} look="cnet" title="Least Common Multiple Calculator" subtitle="Compute the LCM (Least Common Multiple) of two or more positive integers.">
      <p className="text-sm text-slate-700 mb-3">Please provide integers separated by a comma ",", and click the "Calculate" button to find their least common multiple.</p>
      <CNetHintBar />
      <div className="cnet-panel">
        <textarea data-testid="lcm-nums" className="cnet-input w-full" rows="4" value={nums} onChange={e => setNums(e.target.value)} style={{ height: 'auto', minHeight: 96 }} />
        <CNetActions onCalc={calc} onClear={clear} testId="lcm" />
      </div>
      {res && res.error && <div className="text-red-600 mt-3">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="lcm-result">
            LCM({res.arr.join(', ')}) = <span className="text-[#4c7b25]">{res.l}</span>
          </div>
          <div className="mt-3 font-bold text-slate-900">Steps:</div>
          <div className="text-slate-800 mt-1">Prime factorization of the numbers:</div>
          <div className="font-mono text-sm mt-1 leading-7" data-testid="lcm-steps">
            {res.arr.map(n => (
              <div key={n}>{n} = {primeStr(n)}</div>
            ))}
          </div>
          <div className="mt-3 font-mono text-sm leading-7">
            <div>LCM({res.arr.join(', ')})</div>
            <div>= {res.maxPrimes.map(({ p, e }) => Array(e).fill(p).join(' × ')).join(' × ')}</div>
            <div>= <b className="text-[#4c7b25]">{res.l}</b></div>
          </div>
          <div className="mt-3 text-slate-800">GCD({res.arr.join(', ')}) = {res.g}</div>
        </CNetResult>
      )}
    </CalcShell>
  );
}
